import React from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Navigation,
  Pagination,
  Autoplay,
  EffectFade,
  EffectCards,
} from "swiper/modules";
import Footer from "../components/Footer";
import heroImg from "../assets/about-hero.jpg";
import { FaHandsHelping, FaLeaf, FaHistory, FaHeart } from "react-icons/fa";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/effect-cards";

const AboutPage = () => {
  const valeurs = [
    {
      title: "Authenticité",
      icon: <FaHistory className="value-icon" />,
      desc: "Des designs fidèles à l'héritage marocain",
    },
    {
      title: "Tradition",
      icon: <FaHandsHelping className="value-icon" />,
      desc: "Savoir-faire transmis depuis des générations",
    },
    {
      title: "Durabilité",
      icon: <FaLeaf className="value-icon" />,
      desc: "Matériaux naturels et processus écologiques",
    },
    {
      title: "Passion",
      icon: <FaHeart className="value-icon" />,
      desc: "Amour du métier dans chaque couture",
    },
  ];

  const stats = [
    {
      number: "72h",
      label: "de travail artisanal",
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12,6 12,12 16,14" />
        </svg>
      ),
    },
    {
      number: "100%",
      label: "matériaux naturels",
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          <path d="M12 2v20" />
          <path d="M2 12h20" />
        </svg>
      ),
    },
    {
      number: "12",
      label: "étapes de fabrication",
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
      ),
    },
    {
      number: "50+",
      label: "artisans partenaires",
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
  ];

  return (
    <div className="about-container">
      {/* Hero Section with Parallax Effect */}
      <motion.div
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        <div className="hero-overlay" />
        <motion.div
          className="hero-content"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <h1>À propos de SAFA Shop</h1>
          <motion.div
            className="divider"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          />
          <p>L'élégance artisanale au service de vos pieds</p>
        </motion.div>
        <motion.div
          className="scroll-hint"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          onClick={() => {
            window.scrollTo({
              top: window.innerHeight,
              behavior: "smooth",
            });
          }}
          style={{ cursor: "pointer" }}
        >
          <span>Découvrir</span>
        </motion.div>
      </motion.div>

      {/* Intro Section */}
      <section className="intro-section" style={{ padding: "0px 20px" }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="intro-content"
        >
          <h2>
            Une passion pour{" "}
            <span className="highlight">l'artisanat marocain</span>
          </h2>
          <div className="ornamental-divider">
            <div className="line" />
            <div className="decorative-element">✻</div>
            <div className="line" />
          </div>
          <p>
            Chez <strong>SAFA</strong>, chaque babouche raconte une histoire
            tissée de passion et de tradition. Nous collaborons étroitement avec
            des maîtres artisans marocains dont le savoir-faire ancestral se
            transmet de génération en génération.
          </p>
          <motion.div className="signature" whileHover={{ scale: 1.05 }}>
            <span>L'équipe SAFA</span>
          </motion.div>
        </motion.div>
      </section>

      {/* Mission Section */}
      <section
        className="mission-section"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "none",
          padding: "0px 20px",
        }}
      >
        <motion.div
          className="mission-card"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2>
            Notre <span className="mission-highlight">mission</span>
          </h2>
          <p>
            Promouvoir l'excellence de l'artisanat marocain sur la scène
            internationale tout en préservant les techniques traditionnelles.
            Nous nous engageons à créer des ponts culturels à travers des
            créations qui allient esthétique ancestrale et confort moderne.
          </p>
          <ul className="mission-list">
            {[
              "Soutenir les artisans locaux",
              "Préserver les techniques traditionnelles",
              "Promouvoir une mode éthique",
              "Créer des pièces intemporelles",
            ].map((item, index) => (
              <motion.li
                key={index}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {item}
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </section>

      {/* Valeurs Section avec Swiper */}
      <section className="values-section">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="section-header"
        >
          <h2>
            Nos <span className="values-highlight">valeurs</span>
          </h2>
          <p>Les principes qui guident notre démarche artisanale</p>
        </motion.div>

        <div className="values-swiper-container">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            navigation={true}
            pagination={{ clickable: true }}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
            }}
            breakpoints={{
              640: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 3,
              },
            }}
            className="values-swiper"
          >
            {valeurs.map((valeur, index) => (
              <SwiperSlide key={index}>
                <motion.div
                  className="value-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="icon-wrapper">{valeur.icon}</div>
                  <h3>{valeur.title}</h3>
                  <p>{valeur.desc}</p>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* Artisanat Section avec Swiper pour les stats */}
      <section className="craft-section">
        <motion.div
          className="craft-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <h2>
            L'<span className="craft-highlight">art</span> derrière chaque paire
          </h2>
          <p>
            Chaque babouche SAFA est le fruit d'un processus méticuleux qui
            demande jusqu'à 48 heures de travail artisanal. De la sélection des
            cuirs les plus nobles aux finitions à la main, nous veillons à ce
            que chaque détail reflète l'excellence du savoir-faire marocain.
          </p>

          <div className="stats-swiper-container">
            <Swiper
              modules={[Navigation, Pagination, Autoplay, EffectCards]}
              effect="cards"
              grabCursor={true}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              pagination={{ clickable: true }}
              className="stats-swiper"
            >
              {stats.map((stat, index) => (
                <SwiperSlide key={index}>
                  <motion.div
                    className="stat-item"
                    whileHover={{ scale: 1.05 }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AboutPage;

// CSS (à ajouter dans votre fichier de styles)
const styles = `
.about-container {
  font-family: 'Cormorant Garamond', serif;
  color: #2c1810;
  margin: 0 auto;
  overflow-x: hidden;
  background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
}

/* Hero Section */
.hero-section {
  position: relative;
  height: 100vh;
  min-height: 600px;
  background-image: linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)), url(${heroImg});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  margin-bottom: 80px;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(0px) !important;
}

.hero-content {
  position: relative;
  z-index: 2;
  padding: 0 20px;
}

.hero-content h1 {
  font-size: 4.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: 1px;
  text-shadow: 2px 2px 8px rgba(0,0,0,0.6);
  font-family: 'Cormorant Garamond', serif;
}

.hero-content p {
  font-size: 1.5rem;
  font-style: italic;
  text-shadow: 1px 1px 4px rgba(0,0,0,0.5);
  font-family: 'Cormorant Garamond', serif;
}

.divider {
  height: 2px;
  width: 100px;
  background: #d4af37;
  margin: 25px auto;
  transform-origin: center;
}

.scroll-hint {
  position: absolute;
  bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.scroll-hint span {
  font-size: 14px;
  margin-bottom: 5px;
  font-family: 'Cormorant Garamond', serif;
}

.scroll-hint::after {
  content: "";
  width: 20px;
  height: 30px;
  border: 2px solid white;
  border-radius: 10px;
}

.scroll-hint::before {
  content: "";
  width: 4px;
  height: 8px;
  background: white;
  border-radius: 2px;
  position: absolute;
  bottom: 8px;
  animation: scrollHint 2s infinite;
}

@keyframes scrollHint {
  0% { transform: translateY(0); opacity: 0; }
  50% { transform: translateY(5px); opacity: 1; }
  100% { transform: translateY(10px); opacity: 0; }
}

/* Intro Section */
.intro-section {
  padding: 60px 20px;
  text-align: center;
  position: relative;
  margin-bottom: 80px;
}

.intro-content {
  max-width: 900px;
  margin: 0 auto;
}

.intro-content h2 {
  font-size: 2.8rem;
  font-weight: 600;
  margin-bottom: 30px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.highlight {
  color: #d4af37;
  font-weight: 600;
  position: relative;
}

.highlight::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 8px;
  background: rgba(212, 175, 55, 0.2);
  z-index: -1;
}

.ornamental-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px 0;
}

.ornamental-divider .line {
  height: 1px;
  width: 100px;
  background: linear-gradient(to right, transparent, #d4af37, transparent);
}

.ornamental-divider .decorative-element {
  margin: 0 15px;
  color: #d4af37;
  font-size: 1.5rem;
}

.intro-content p {
  font-size: 18px;
  line-height: 1.8;
  margin-bottom: 40px;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

.signature {
  display: inline-block;
  font-style: italic;
  font-size: 16px;
  padding: 10px 30px;
  border-top: 1px solid #d4af37;
  cursor: default;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

/* Mission Section */
.mission-section {
  position: relative;
  padding: 100px 20px;
  margin-bottom: 100px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  overflow: hidden;
}

.mission-pattern {
  position: absolute;
  top: 0;
  right: 0;
  width: 40%;
  height: 100vh;
  background: rgba(212, 175, 55, 0.05);
  opacity: 0.6;
}

.mission-card {
  position: relative;
  z-index: 2;
  max-width: 700px;
  background: rgba(255, 255, 255, 0.1);
  padding: 50px;
  border-radius: 20px;
}

.mission-card h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 30px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.mission-highlight {
  position: relative;
}

.mission-highlight::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 12px;
  background: rgba(212, 175, 55, 0.2);
  z-index: -1;
}

.mission-card p {
  font-size: 18px;
  line-height: 1.8;
  margin-bottom: 30px;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

.mission-list {
  list-style: none;
  padding: 0;
}

.mission-list li {
  position: relative;
  padding-left: 30px;
  margin-bottom: 15px;
  font-size: 18px;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

.mission-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #d4af37;
  font-size: 1.5rem;
}

/* Values Section */
.values-section {
  padding: 80px 20px;
  text-align: center;
  margin-bottom: 80px;
}

.section-header h2 {
  font-size: 2.8rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.values-highlight {
  position: relative;
}

.values-highlight::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 12px;
  background: rgba(212, 175, 55, 0.2);
  z-index: -1;
}

.section-header p {
  font-size: 16px;
  color: #5a4a3a;
  margin-bottom: 50px;
  font-style: italic;
  font-family: 'Cormorant Garamond', serif;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
}

.value-card {
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(15px);
  padding: 40px 30px;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  border: 1px solid rgba(212, 175, 55, 0.4);
  transition: all 0.3s ease;
  cursor: default;
}

.value-card:hover {
  box-shadow: 0 8px 25px rgba(212, 175, 55, 0.3);
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.4);
}

.icon-wrapper {
  width: 70px;
  height: 70px;
  margin: 0 auto 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(212, 175, 55, 0.3);
  border-radius: 50%;
  border: 2px solid rgba(212, 175, 55, 0.5);
  backdrop-filter: blur(10px);
}

.value-icon {
  font-size: 1.8rem;
  color: #2c1810;
}

.value-card h3 {
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: #2c1810;
  font-weight: 600;
  font-family: 'Cormorant Garamond', serif;
}

.value-card p {
  font-size: 16px;
  line-height: 1.6;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

/* Craft Section */
.craft-section {
  padding: 100px 20px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  margin-bottom: 80px;
  border: 1px solid rgba(212, 175, 55, 0.2);
}

.craft-content {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}

.craft-content h2 {
  font-size: 2.8rem;
  font-weight: 600;
  margin-bottom: 30px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.craft-highlight {
  position: relative;
}

.craft-highlight::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 12px;
  background: rgba(212, 175, 55, 0.2);
  z-index: -1;
}

.craft-content p {
  font-size: 18px;
  line-height: 1.8;
  margin-bottom: 50px;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

.stats-container {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 50px;
  margin-top: 50px;
}

.stat-item {
  text-align: center;
  min-width: 150px;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 600;
  color: #d4af37;
  margin-bottom: 10px;
  font-family: 'Cormorant Garamond', serif;
}

.stat-label {
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

/* Swiper Styles modernes */
.values-swiper-container, .stats-swiper-container {
  position: relative;
  width: 100%;
  margin: 0 auto;
  padding: 0 40px;
}

.values-swiper, .stats-swiper {
  width: 100%;
  padding-bottom: 60px;
}

/* Navigation buttons */
.values-swiper .swiper-button-next,
.values-swiper .swiper-button-prev,
.stats-swiper .swiper-button-next,
.stats-swiper .swiper-button-prev {
  color: #2c1810;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(212, 175, 55, 0.4);
  transition: all 0.3s ease;
}

.values-swiper .swiper-button-next:hover,
.values-swiper .swiper-button-prev:hover,
.stats-swiper .swiper-button-next:hover,
.stats-swiper .swiper-button-prev:hover {
  background: rgba(212, 175, 55, 0.4);
  color: #2c1810;
  transform: scale(1.1);
  box-shadow: 0 4px 16px rgba(212, 175, 55, 0.4);
}

.values-swiper .swiper-button-next::after,
.values-swiper .swiper-button-prev::after,
.stats-swiper .swiper-button-next::after,
.stats-swiper .swiper-button-prev::after {
  font-size: 18px;
  font-weight: bold;
}

/* Pagination */
.values-swiper .swiper-pagination-bullet,
.stats-swiper .swiper-pagination-bullet {
  background: #2c1810;
  opacity: 0.6;
  transition: all 0.3s ease;
}

.values-swiper .swiper-pagination-bullet-active,
.stats-swiper .swiper-pagination-bullet-active {
  opacity: 1;
  transform: scale(1.2);
  background: #d4af37;
}

/* Slides */
.values-swiper .swiper-slide,
.stats-swiper .swiper-slide {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

/* Stats Swiper spécifique */
.stats-swiper {
  max-width: 600px;
  margin: 0 auto;
}

.stats-swiper .swiper-slide {
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(212, 175, 55, 0.4);
  padding: 40px 30px;
  margin: 20px;
}

.stat-item {
  text-align: center;
  width: 100%;
}

.stat-item .stat-icon {
  font-size: 3rem;
  margin-bottom: 20px;
  display: block;
}

.stat-item .stat-number {
  font-size: 3rem;
  font-weight: 600;
  color: #d4af37;
  margin-bottom: 10px;
  line-height: 1;
  font-family: 'Cormorant Garamond', serif;
}

.stat-item .stat-label {
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #5a4a3a;
  font-weight: 600;
  font-family: 'Cormorant Garamond', serif;
}

/* Responsive */
@media (max-width: 768px) {
  .values-swiper-container, .stats-swiper-container {
    padding: 0 20px;
  }
  
  .values-swiper .swiper-button-next,
  .values-swiper .swiper-button-prev,
  .stats-swiper .swiper-button-next,
  .stats-swiper .swiper-button-prev {
    width: 40px;
    height: 40px;
  }
  
  .values-swiper .swiper-button-next::after,
  .values-swiper .swiper-button-prev::after,
  .stats-swiper .swiper-button-next::after,
  .stats-swiper .swiper-button-prev::after {
    font-size: 14px;
  }
  
  .stat-item .stat-number {
    font-size: 2.5rem;
  }
  
  .stat-item .stat-icon {
    font-size: 2.5rem;
  }
}

@media (max-width: 768px) {
  .hero-content h1 {
    font-size: 3rem;
  }
  
  .hero-content p {
    font-size: 18px;
  }
  
  .intro-content h2, .mission-card h2, .section-header h2, .craft-content h2 {
    font-size: 2rem;
  }
  
  .values-grid {
    grid-template-columns: 1fr;
  }
  
  .mission-pattern {
    width: 100%;
    opacity: 0.2;
  }
  
  .mission-card {
    padding: 30px 20px;
    width: 100%;
    box-sizing: border-box;
    border-radius: 20px;
  }
  
  .stats-container {
    gap: 30px;
  }
  
  .stat-item {
    min-width: 120px;
  }
}
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
